import {
  tableV2NestedCarrierRowSx,
  tableV2NestedContentSx,
  tableV2NestedDepthSx,
  tableV2NestedDetailCellSx,
} from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  formatTableV2DateTime,
  formatTableV2PassRate,
  tableV2RelativeTimeSx,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { r, s, typographyTokens } from '@/theme/tokens';
import { Box, Chip, Text, getStatusChipColor, resolveStatusChipTone } from '@/ui/primitives';
import { metricsStatusOrder } from '@flowqa/shared';
import type {
  ExecutionRunDto,
  ExecutionStatusKey,
  SuiteRunStatus,
  TestRunStatus,
} from '@flowqa/shared';
import type { ReactElement } from 'react';

const statusColorVars = {
  neutral: 'var(--color-neutral)',
  green: 'var(--color-green)',
  red: 'var(--color-red)',
  orange: 'var(--color-orange)',
  brown: 'var(--color-brown)',
  teal: 'var(--color-teal)',
  purple: 'var(--color-purple)',
  pink: 'var(--color-pink)',
  blue: 'var(--color-blue)',
} as const;

const resolveStatusColorVar = (
  status: SuiteRunStatus | TestRunStatus | ExecutionStatusKey | null | undefined,
): string => statusColorVars[getStatusChipColor(status)];

export type ExecutionRunCounts = {
  total: number;
  passed: number;
  failed: number;
  errored: number;
  skipped: number;
  untested: number;
};

export const countSuites = (row: ExecutionRunDto): ExecutionRunCounts => ({
  total:
    (row.suiteRunPassed ?? 0) +
    (row.suiteRunFailed ?? 0) +
    (row.suiteRunErrored ?? 0) +
    (row.suiteRunSkipped ?? 0) +
    (row.suiteRunUntested ?? 0),
  passed: row.suiteRunPassed ?? 0,
  failed: row.suiteRunFailed ?? 0,
  errored: row.suiteRunErrored ?? 0,
  skipped: row.suiteRunSkipped ?? 0,
  untested: row.suiteRunUntested ?? 0,
});

export const countTests = (row: ExecutionRunDto): ExecutionRunCounts => ({
  total:
    (row.testRunPassed ?? 0) +
    (row.testRunFailed ?? 0) +
    (row.testRunErrored ?? 0) +
    (row.testRunSkipped ?? 0) +
    (row.testRunUntested ?? 0),
  passed: row.testRunPassed ?? 0,
  failed: row.testRunFailed ?? 0,
  errored: row.testRunErrored ?? 0,
  skipped: row.testRunSkipped ?? 0,
  untested: row.testRunUntested ?? 0,
});

export const formatPassRate = (counts: ExecutionRunCounts): string => formatTableV2PassRate(counts);

export const formatUploadedAt = (value: Date | string | null | undefined): string =>
  formatTableV2DateTime(value);

export const relativeTimeSx = tableV2RelativeTimeSx;

export const nestedCarrierRowSx = tableV2NestedCarrierRowSx;
export const nestedDetailCellSx = tableV2NestedDetailCellSx;
export const nestedDetailContentSx = tableV2NestedContentSx;
export const nestedTestTableFrameSx = tableV2NestedDepthSx[2].frame;
export const nestedTestTableSurfaceSx = tableV2NestedDepthSx[2].table;

export const StatusChip = ({
  label,
  status,
}: {
  label: string;
  status: SuiteRunStatus | TestRunStatus | null | undefined;
}): ReactElement => {
  return (
    <Chip
      size="small"
      tone={resolveStatusChipTone(status)}
      label={label}
      sx={{
        border: `1px solid ${resolveStatusColorVar(status)}`,
        borderRadius: r('sm'),
        backgroundColor: 'transparent',
        color: resolveStatusColorVar(status),
        fontWeight: typographyTokens.caption.fontWeight,
        height: '30px',
        '& .MuiChip-label': {
          color: 'inherit',
          px: s('lg'),
          py: s('xs'),
        },
        '& .MuiChip-label .MuiTypography-root': {
          color: resolveStatusColorVar(status),
          fontWeight: typographyTokens.caption.fontWeight,
        },
        '&:hover, &.Mui-focusVisible, &:active': {
          backgroundColor: 'transparent',
        },
      }}
    />
  );
};

const PassRateStatusBar = ({ counts }: { counts: ExecutionRunCounts }): ReactElement => {
  const hasCounts = counts.total > 0;
  const statusSegments = metricsStatusOrder.map((statusKey) => ({
    key: statusKey,
    value: counts[statusKey],
  })) satisfies Array<{ key: ExecutionStatusKey; value: number }>;
  const segments = statusSegments.filter((segment) => segment.value > 0);

  return (
    <Box
      aria-hidden="true"
      sx={{
        backgroundColor: (theme) => theme.app.state.disabledBg,
        borderRadius: r('pill'),
        display: 'flex',
        height: s('sm'),
        mt: s('md'),
        overflow: 'hidden',
        width: '100%',
      }}
    >
      {segments.map((segment) => (
        <Box
          key={segment.key}
          sx={{
            backgroundColor: resolveStatusColorVar(segment.key),
            height: '100%',
            width: hasCounts ? `${String((segment.value / counts.total) * 100)}%` : '0%',
          }}
        />
      ))}
    </Box>
  );
};

export const PassRateCell = ({ counts }: { counts: ExecutionRunCounts }): ReactElement => {
  return (
    <Box sx={{ minWidth: s('3xl') }}>
      <Box sx={{ alignItems: 'baseline', display: 'flex', gap: s('sm') }}>
        <Text role="body" component="span" sx={{ fontWeight: typographyTokens.body.fontWeight }}>
          {formatPassRate(counts)}
        </Text>
        <Text role="captionSmall" color="textSecondary" component="span">
          {counts.total > 0 ? `(${String(counts.passed)}/${String(counts.total)})` : '(-)'}
        </Text>
      </Box>
      <PassRateStatusBar counts={counts} />
    </Box>
  );
};
